import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

// A GENERIC pick-and-order list — born as the Schedule Visit slideover's
// own "Forms to Complete" widget (TASK-238.2, doc-39 §5: "Add another
// form", add/remove, order preserved; TASK-241 added drag-reorder) and
// generalised in TASK-239 (decision-66) so Settings › Products' own
// inspection-forms editor can reuse it rather than forking the add/
// remove/reorder behaviour a second time. Each chosen row is an <li>
// carrying a hidden input, named by `inputNameValue`, in submission
// order — adding/removing/reordering just moves elements around in the
// DOM, so the DOM order IS the order that submits on the outer form;
// nothing here renumbers anything by hand or posts anywhere on its own
// (contrast sortable_list_controller.js, which persists via its own PATCH
// — this list's order is only ever read at the surrounding form's
// submit, same as the form builder's own field reorder reusing the same
// `sortablejs` library). No keyboard fallback — drag only (TASK-241's own
// call, unchanged by the generalisation).
export default class extends Controller {
  static targets = ["list", "candidate", "item"]
  static values = { inputName: String }

  connect() {
    this.sortable = Sortable.create(this.listTarget, {
      handle: "[data-ordered-picker-handle]",
      animation: 150,
      ghostClass: "opacity-50"
    })
  }

  disconnect() {
    this.sortable?.destroy()
  }

  add() {
    const select = this.candidateTarget
    const option = select.selectedOptions[0]
    if (!option || !option.value) return

    this.listTarget.appendChild(this.buildRow(option.value, option.textContent))
    option.remove()
    select.selectedIndex = 0
  }

  remove(event) {
    const item = event.target.closest("[data-ordered-picker-target='item']")
    if (!item) return

    const option = document.createElement("option")
    option.value = item.dataset.id
    option.textContent = item.querySelector("span").textContent
    this.candidateTarget.appendChild(option)

    item.remove()
  }

  buildRow(id, name) {
    const li = document.createElement("li")
    li.setAttribute("data-ordered-picker-target", "item")
    li.setAttribute("data-id", id)
    li.className = "flex items-center gap-2.5 px-3 py-2.5"

    const handle = document.createElement("span")
    handle.className = "flex-shrink-0 cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
    handle.setAttribute("data-ordered-picker-handle", "")
    handle.setAttribute("aria-label", "Drag to reorder")
    handle.innerHTML = '<svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M3 7.5L7.5 3m0 0L12 7.5M7.5 3v13.5m13.5 0L16.5 21m0 0L12 16.5m4.5 4.5V7.5"/></svg>'

    const input = document.createElement("input")
    input.type = "hidden"
    input.name = this.inputNameValue
    input.value = id

    const span = document.createElement("span")
    span.className = "flex-1 text-sm font-medium text-gray-900"
    span.textContent = name

    const button = document.createElement("button")
    button.type = "button"
    button.className = "text-gray-400 hover:text-gray-600"
    button.setAttribute("data-action", "click->ordered-picker#remove")
    button.setAttribute("aria-label", `Remove ${name}`)
    button.innerHTML = '<svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 18 18 6M6 6l12 12"/></svg>'

    li.append(handle, input, span, button)
    return li
  }
}
